// Types
export interface RegistrationData {
  firstname: string;
  lastname: string;
  jobTitle?: string;
  company?: string;
  mobilePhone: string;
  email: string;
}

export interface RegistrationResponse {
  success: boolean;
  message: string;
  data: {
    userId: string;
    registrationId: string;
    user: {
      firstname: string;
      lastname: string;
      displayName: string;
      jobTitle: string;
      company: string;
      mobilePhone: string;
      email: string;
      createdAt: any;
      qrCode: string;
    }
  }
}

// API Service class
class ApiService {
  private baseUrl: string;

  constructor() {
    const baseUrl = process.env.REACT_APP_API_URL;
    if (!baseUrl) {
      throw new Error('API URL not configured. Please set REACT_APP_API_URL in .env file.');
    }
    this.baseUrl = baseUrl;
  }

  // Register a new user
  async register(data: RegistrationData): Promise<RegistrationResponse> {
    const response = await fetch(`${this.baseUrl}/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.message || 'Registration failed');
    }

    return response.json();
  }

  // Get all registrations
  async getAllRegistrations(): Promise<RegistrationResponse[]> {
    const response = await fetch(`${this.baseUrl}/registrations`);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.message || 'Failed to fetch registrations');
    }

    return response.json();
  }

  // Get specific registration
  async getRegistration(id: string): Promise<RegistrationResponse> {
    const response = await fetch(`${this.baseUrl}/registrations/${id}`);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.message || 'Failed to fetch registration');
    }

    return response.json();
  }

  // Update registration status
  async updateRegistrationStatus(
    id: string,
    status: 'registered' | 'checked-in' | 'cancelled'
  ): Promise<RegistrationResponse> {
    const response = await fetch(`${this.baseUrl}/registrations/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.message || 'Failed to update registration status');
    }

    return response.json();
  }
}

// Export a single instance
export const api = new ApiService(); 